import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { RoomBookingFormModal } from './RoomBookingFormModal';
import { getRoomBookingColumns } from './roomBookingColumns';
import { useRoomBookings } from '../../hooks/useRoomBookings';
import { useInterventionRooms } from '../../hooks/useInterventionRooms';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import type { RoomBookingRequest, RoomBookingResponse } from '../../api/generated';
import {
  BookingListSection,
  DeleteBookingModal,
  formatRecurrenceSummary,
  isRecurringTime,
  useBookingList
} from '../Bookings/shared';

const TABLE_MIN_WIDTH = 920;
const KEY_PREFIX = 'room-booking';

export const RoomBookings = () => {
  const { t } = useTranslation();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  } = useRoomBookings(routeLocationId ?? null);
  const { rooms } = useInterventionRooms(locationId);
  const { competences } = useStaffCompetences();

  const list = useBookingList<RoomBookingResponse, RoomBookingRequest>({
    keyPrefix: KEY_PREFIX,
    createBooking,
    updateBooking,
    deleteBooking,
    isRecurringPayload: (payload) => isRecurringTime(payload.time)
  });

  const activeProfessions = useMemo(() => competences.filter((c) => !c.archived), [competences]);
  const roomNameById = useMemo(
    () => new Map(rooms.map((room) => [room.id, room.description])),
    [rooms]
  );

  const columns = useMemo(
    () =>
      getRoomBookingColumns(t, {
        roomNameById,
        onEdit: list.openEdit,
        onDelete: list.requestDelete
      }),
    [t, roomNameById, list.openEdit, list.requestDelete]
  );

  return (
    <BookingListSection
      keyPrefix={KEY_PREFIX}
      isLoading={isLoading}
      errorMessage={loadError}
      locationId={locationId}
      columns={columns}
      bookings={bookings}
      minWidth={TABLE_MIN_WIDTH}
      onAdd={list.openCreate}
      onRetry={() => {
        refresh();
      }}
    >
      {list.isFormOpen && (
        <RoomBookingFormModal
          booking={list.editingBooking}
          rooms={rooms}
          professions={activeProfessions}
          onSave={(payload) => {
            list.handleSave(payload);
          }}
          onClose={list.closeForm}
        />
      )}

      {list.deleteTarget && (
        <DeleteBookingModal
          titleKey="room-booking-delete-title"
          confirmKey="room-booking-delete-confirm"
          messageKey="room-booking-delete-message"
          recurringMessageKey="room-booking-delete-recurring-message"
          name={roomNameById.get(list.deleteTarget.roomId) ?? list.deleteTarget.roomId}
          recurrenceSummary={
            isRecurringTime(list.deleteTarget.time)
              ? formatRecurrenceSummary(t, list.deleteTarget.time)
              : undefined
          }
          onConfirm={() => {
            list.handleConfirmDelete();
          }}
          onClose={list.cancelDelete}
        />
      )}
    </BookingListSection>
  );
};
